import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { priceService } from '../../services/priceService';
import { regionService } from '../../services/regionService';
import { vehicleService } from '../../services/vehicleService';
import { carrierService } from '../../services/carrierService';
import { toast } from 'react-toastify';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const PriceList = () => {
  const [prices, setPrices] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    limit: 10
  });
  const [isLoading, setIsLoading] = useState(true);
  const [regions, setRegions] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [filters, setFilters] = useState({
    carrierId: '',
    fromRegionId: '',
    toRegionId: '',
    vehicleTypeId: '',
    page: 1,
    limit: 10
  });

  useEffect(() => {
    fetchPrices();
    fetchRegions();
    fetchVehicleTypes();
    fetchCarriers();
  }, []);

  useEffect(() => {
    fetchPrices();
  }, [filters]);

  const fetchCarriers = async () => {
    try {
      const response = await carrierService.getCarriers({ isActive: true });
      if (response.data) {
        setCarriers(response.data.rows);
      }
    } catch (error) {
      toast.error('Taşıyıcılar yüklenirken bir hata oluştu');
      console.error('Error fetching carriers:', error);
    }
  };

  const fetchRegions = async () => {
    try {
      const response = await regionService.getRegions();
      if (response.data) {
        setRegions(response.data.regions);
      }
    } catch (error) {
      toast.error('Bölgeler yüklenirken bir hata oluştu');
      console.error('Error fetching regions:', error);
    }
  };

  const fetchVehicleTypes = async () => {
    try {
      const response = await vehicleService.getVehicleTypes();
      if (response.data) {
        setVehicleTypes(response.data.vehicleTypes);
      }
    } catch (error) {
      toast.error('Araç tipleri yüklenirken bir hata oluştu');
      console.error('Error fetching vehicle types:', error);
    }
  };

  const fetchPrices = async () => {
    setIsLoading(true);
    try {
      const response = await priceService.getPrices(filters);
      console.log('API Response:', response);

      if (response && response.success && response.data) {
        if (response.data) {
          setPrices(response.data.prices);
          if (response.data.pagination) {
            setPagination(response.data.pagination);
          } else {
            setPagination({
              currentPage: parseInt(filters.page),
              totalPages: Math.ceil(response.data.count / filters.limit),
              total: response.data.count,
              limit: parseInt(filters.limit)
            });
          }
        } else {
          console.log('No rows found in data');
          setPrices([]);
          setPagination({
            currentPage: 1,
            totalPages: 1,
            total: 0,
            limit: parseInt(filters.limit)
          });
        }
      } else {
        console.log('Invalid response structure');
        setPrices([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          total: 0,
          limit: parseInt(filters.limit)
        });
      }
    } catch (error) {
      toast.error('Fiyatlar yüklenirken bir hata oluştu');
      console.error('Error fetching prices:', error);
      setPrices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1 // Filtre değiştiğinde ilk sayfaya dön
    }));
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= pagination.totalPages) {
      setFilters(prev => ({
        ...prev,
        page: newPage
      }));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu fiyatı silmek istediğinize emin misiniz?')) {
      try {
        await priceService.deletePrice(id);
        toast.success('Fiyat başarıyla silindi');
        fetchPrices();
      } catch (error) {
        toast.error('Fiyat silinirken bir hata oluştu');
        console.error('Error deleting price:', error);
      }
    }
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: currency || 'EUR' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR');
  };

  return (
    <div>
      <div className="pb-5 border-b border-gray-200 sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Fiyatlar</h1>
        <div className="mt-3 sm:mt-0 sm:ml-4 flex space-x-3">
          <Link
            to="/prices/bulk"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Toplu Fiyat Girişi
          </Link>
          <Link
            to="/prices/create"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Yeni Fiyat Ekle
          </Link>
        </div>
      </div>

      {/* Filtreler */}
      <div className="mt-4 bg-white shadow rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label htmlFor="carrierId" className="block text-sm font-medium text-gray-700">
              Taşıyıcı
            </label>
            <select
              id="carrierId"
              name="carrierId"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.carrierId}
              onChange={handleFilterChange}
            >
              <option value="">Tümü</option>
              {carriers.map(carrier => (
                <option key={carrier.id} value={carrier.id}>{carrier.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="fromRegionId" className="block text-sm font-medium text-gray-700">
              Başlangıç Bölgesi
            </label>
            <select
              id="fromRegionId"
              name="fromRegionId"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.fromRegionId}
              onChange={handleFilterChange}
            >
              <option value="">Tümü</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>{region.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="toRegionId" className="block text-sm font-medium text-gray-700">
              Varış Bölgesi
            </label>
            <select
              id="toRegionId"
              name="toRegionId"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.toRegionId}
              onChange={handleFilterChange}
            >
              <option value="">Tümü</option>
              {regions.map(region => (
                <option key={region.id} value={region.id}>{region.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="vehicleTypeId" className="block text-sm font-medium text-gray-700">
              Araç Tipi
            </label>
            <select
              id="vehicleTypeId"
              name="vehicleTypeId"
              className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              value={filters.vehicleTypeId}
              onChange={handleFilterChange}
            >
              <option value="">Tümü</option>
              {vehicleTypes.map(type => (
                <option key={type.id} value={type.id}>{type.name} (Kapasite: {type.capacity})</option>
              ))}
            </select>
          </div>
          <div className="flex items-end md:col-span-4">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              onClick={() => setFilters({ carrierId: '', fromRegionId: '', toRegionId: '', vehicleTypeId: '', page: 1, limit: 10 })}
            >
              Filtreleri Temizle
            </button>
          </div>
        </div>
      </div>

      {/* Fiyat Listesi */}
      <div className="mt-4 bg-white shadow overflow-hidden sm:rounded-lg">
        {isLoading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
          </div>
        ) : prices.length === 0 ? (
          <div className="text-center p-12">
            <p className="text-gray-500">Fiyat bulunamadı.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taşıyıcı
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Başlangıç Bölgesi
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Varış Bölgesi
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Araç Tipi
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fiyat
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Geçerlilik Başlangıcı
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Geçerlilik Bitişi
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {prices.map((price) => (
                  <tr key={price.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {price.Carrier?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {price.fromRegion?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {price.toRegion?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {price.VehicleType?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(price.price, price.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(price.valid_from)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(price.valid_to)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <Link
                          to={`/prices/${price.id}/edit`}
                          className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          <PencilIcon className="h-4 w-4" aria-hidden="true" />
                        </Link>
                        <button
                          type="button"
                          className="inline-flex items-center p-1.5 border border-transparent rounded-full shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          onClick={() => handleDelete(price.id)}
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sayfalama */}
      {!isLoading && prices.length > 0 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Önceki
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 ${
                pagination.currentPage === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              Sonraki
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Toplam <span className="font-medium">{pagination.total}</span> fiyattan{' '}
                <span className="font-medium">{(pagination.currentPage - 1) * pagination.limit + 1}</span>-
                <span className="font-medium">
                  {Math.min(pagination.currentPage * pagination.limit, pagination.total)}
                </span>{' '}
                arası gösteriliyor
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    pagination.currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="sr-only">Önceki</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                </button>
                {/* Sayfa numaraları */}
                {[...Array(pagination.totalPages).keys()].map((page) => (
                  <button
                    key={page + 1}
                    onClick={() => handlePageChange(page + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border ${
                      pagination.currentPage === page + 1
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    } text-sm font-medium`}
                  >
                    {page + 1}
                  </button>
                ))}
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 ${
                    pagination.currentPage === pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="sr-only">Sonraki</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PriceList;
